import { ArrowLeftIcon } from "@chakra-ui/icons";
import { Box, Flex, Spinner, Text, useBreakpointValue } from "@chakra-ui/react";
import GCPImage from "components/GCPImage";
import { carVersionI18n, reduceCarsToOneLevelAddVersion } from "lib/cars";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import appStore from "store";
import * as ga from "../../lib/analytics";
import Sheet from "react-modal-sheet";
import { getAvailableCarIdsForStation } from "lib/order";
import NewYearBanner from "components/NewYearBanner";
import { deviceType } from "react-device-detect";
import { sendAppierEvent } from "lib/appier";
import { isAfter } from "date-fns";

const NoCarGroup = ({ carInfo, carList, groupPrice, top_models }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;

  const {
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    tempSelectedZoneId,
  } = appStore;

  const carIds = carList?.map((car) => car.id);

  const modelName = carInfo?.model_name
    ?.replace("Model", "Model ")
    ?.replace("(", " (");

  const carTypeKey = modelName?.replace(/\s/g, "")?.toLowerCase() + "_type";

  const isHot = top_models?.includes(carInfo.model_id);

  let title = `${carInfo?.brand} ${carInfo?.model_name}`;
  let subTitle = carVersionI18n(carInfo?.version, t) || "";

  if (carInfo?.version && carInfo?.version.includes("座")) {
    subTitle = `${carInfo?.version?.replace("座", ` ${t("titleSeats")}`) || ""}`;
  }

  if (subTitle.toString().includes("Seats)")) {
    subTitle = subTitle.split("Seats) ")[1] || "";
  }

  return (
    <Box
      mt="16px"
      display="flex"
      flexDirection="column"
      bg="#F3F4F9"
      p="16px"
      minW="358px"
      w="100%"
      minH="284px"
      borderRadius="16px"
      pos="relative"
      cursor="pointer"
      align="start"
      onClick={() => {
        appStore.tempSelectedZoneId = null;
        appStore.tempSelectedReturnZoneId = null;
        appStore.selectedCarIds = carIds;
        router.push({
          pathname: "/rent/nocar",
          query: {
            ...router.query,
            group: carInfo.group_lv1_id,
          },
        });
        fbq("track", "ViewContent");
        ga.event({
          action: "view_item",
        });
      }}
    >
      {/* tag */}
      <Flex mb="16px">
        {isHot && (
          <Flex pos="relative">
            <Flex
              px="8px"
              py="4px"
              borderRadius="13px"
              bg="#BDC3D0"
              mr="8px"
              align="center"
            >
              <Text
                fontSize="12px"
                fontWeight="700"
                lineHeight="16px"
                color="white"
              >
                {t("Car_card_hot")}
              </Text>
            </Flex>
          </Flex>
        )}
        <Flex
          px="8px"
          py="4px"
          h="26px"
          borderRadius="13px"
          bg="#BDC3D0"
          mr="8px"
          pos="relative"
          align="center"
        >
          <Text
            fontSize="12px"
            fontWeight="700"
            lineHeight="16px"
            color="white"
          >
            {t("Rent_card_statetext_noCar")}
          </Text>
        </Flex>
      </Flex>

      <Flex align="center" justify="center" h="145px" pos="relative">
        <GCPImage src={carInfo?.image} alt="car" w="313px" />
      </Flex>

      <Flex flexDir="column" minH="108px" w="auto">
        <Flex flex="1"></Flex>

        <Flex justify="space-between" mt="8px">
          <Flex flexDir="column" justify="end">
            <Text
              textAlign="start"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
              color="#333333"
              mb="2px"
            >
              {title}
            </Text>
            <Text
              textAlign="start"
              fontSize="14px"
              fontWeight="400"
              lineHeight="20px"
              color="#748598"
            >
              {subTitle && subTitle.toString().includes("座)")
                ? subTitle.split("座) ")[1] || ""
                : subTitle.toString().includes("Seats)")
                  ? subTitle.split("Seats) ")[1] || ""
                  : subTitle}
            </Text>
            <Box mt="8px">
              <Flex align="center">
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_seats.png" />
                <Text
                  mr="16px"
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {carInfo.note.seat}
                  {t("peopleSeat")}
                </Text>
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_car.png" />
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {t(carTypeKey)}
                </Text>
              </Flex>
            </Box>
          </Flex>
          <Flex justify="end" flexDir={{ base: "column", md: "column" }}>
            <Text
              textAlign="right"
              textDecoration="line-through"
              color="#748598"
              fontWeight="400"
              fontSize="14px"
              lineHeight="20px"
            >
              NT$ {carInfo?.displayPrice?.origin_price?.toLocaleString()}
            </Text>
            <Text
              whiteSpace="nowrap"
              as="span"
              color="#BDC3D0"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
            >
              NT${" "}
              {groupPrice
                .find(
                  (group) =>
                    group.model_name ===
                    `${carInfo?.model_name}${carInfo?.version !== null ? " " + carInfo?.version : ""}`
                )
                ?.avgAmount?.toLocaleString()}{" "}
              &nbsp;/{t("days")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

const CarGroup = ({ carInfo, carList, groupPrice, top_models, zoneDetail }) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { locale } = router;
  const {
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    tempSelectedZoneId,
  } = appStore;

  const carNum = carList?.length;
  const carIds = carList?.map((car) => car.id);

  const modelName = carInfo?.model_name
    ?.replace("Model", "Model ")
    ?.replace("(", " (");

  const carTypeKey = modelName?.replace(/\s/g, "")?.toLowerCase() + "_type";

  const isHot = top_models?.includes(carInfo.model_id);

  let title = `${carInfo?.brand} ${carInfo?.model_name}`;
  let subTitle = carVersionI18n(carInfo?.version, t) || "";

  if (carInfo?.version && carInfo?.version.includes("座")) {
    subTitle = `${carInfo?.version?.replace("座", ` ${t("titleSeats")}`) || ""}`;
  }

  return (
    <Box
      mt="16px"
      display="flex"
      flexDirection="column"
      w="100%"
      minW="358px"
      minH="284px"
      bg="#F3F4F9"
      borderRadius="16px"
      p="16px"
      cursor="pointer"
      onClick={() => {
        appStore.selectedCarIds = carIds;
        appStore.selectedZoneId = tempSelectedZoneId;
        appStore.selectedCity = zoneDetail.level_1;
        appStore.selectedZone = zoneDetail.level_2;
        appStore.selectedStation = zoneDetail.level_3;
        appStore.selectedReturnZoneId = tempSelectedZoneId;
        appStore.selectedReturnCity = zoneDetail.level_1;
        appStore.selectedReturnZone = zoneDetail.level_2;
        appStore.selectedReturnStation = zoneDetail.level_3;
        appStore.zoneOpenSt = zoneDetail.openSt;
        appStore.zoneOpenEd = zoneDetail.openEd;
        appStore.zoneDetail = zoneDetail;
        router.push({
          pathname: "/station/carInfo",
          query: {
            ...router.query,
            group: carInfo.group_lv1_id,
          },
        });
        fbq("track", "ViewContent");
        ga.event({
          action: "view_item",
        });
      }}
    >
      {/* 熱門、免費取消 tag */}
      <Flex mb="16px" h="26px">
        {isHot && (
          <Flex
            px="8px"
            py="4px"
            borderRadius="13px"
            bg="#333333"
            mr="8px"
            align="center"
          >
            <Text
              fontSize="12px"
              fontWeight="700"
              lineHeight="16px"
              color="white"
            >
              {t("Car_card_hot")}
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex align="center" justify="center" h="145px">
        <GCPImage src={carInfo?.image} alt="car" w="313px" />
      </Flex>

      <Flex flexDir="column" minH="108px" w="auto" justify="end">
        <Flex h="30px"></Flex>

        <Flex justify="space-between" mt="8px">
          <Flex flexDir="column" justify="end">
            <Text
              textAlign="start"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
              color="#333333"
              mb="2px"
            >
              {title}
            </Text>
            <Text
              textAlign="start"
              fontSize="14px"
              fontWeight={
                carInfo?.model_id == 31 || carInfo?.model_id == 32
                  ? "700"
                  : "400"
              }
              lineHeight="20px"
              color={
                carInfo?.model_id == 31 || carInfo?.model_id == 32
                  ? "#D12E25"
                  : "#333333"
              }
            >
              {subTitle && subTitle.toString().includes("座)")
                ? subTitle.split("座) ")[1] || ""
                : subTitle.toString().includes("Seats)")
                  ? subTitle.split("Seats) ")[1] || ""
                  : subTitle}
            </Text>
            <Box mt="8px">
              <Flex align="center">
                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_seats.png" />
                <Text
                  mr="16px"
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {carInfo?.note?.seat}
                  {t("peopleSeat")}
                </Text>

                <GCPImage mr="2px" w="16px" src="/images/rent/Icon_car.png" />
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="400"
                  color="#748598"
                >
                  {t(carTypeKey)}
                </Text>
              </Flex>
            </Box>
          </Flex>
          <Flex justify="end" flexDir={{ base: "column", md: "column" }}>
            {carNum <= 5 && (
              <Flex
                ml="auto"
                w="auto"
                zIndex="99"
                py="2px"
                px="4px"
                bg="#D12E25"
                borderRadius="4px"
                mb="4px"
              >
                <Text
                  fontSize="12px"
                  lineHeight="16px"
                  fontWeight="700"
                  color="white"
                >
                  {t("Rent_card_buttonchip_only1")} {carNum}{" "}
                  {t("Rent_card_buttonchip_only2")}
                </Text>
              </Flex>
            )}
            <Text
              mb="2px"
              textAlign="right"
              textDecoration="line-through"
              color="#748598"
              fontWeight="400"
              fontSize="14px"
              lineHeight="20px"
            >
              NT$ {carInfo?.displayPrice?.origin_price?.toLocaleString()}
            </Text>
            <Text
              whiteSpace="nowrap"
              as="span"
              color="#333333"
              fontSize="16px"
              fontWeight="700"
              lineHeight="24px"
            >
              NT${" "}
              {groupPrice
                .find(
                  (group) =>
                    group.model_name ===
                    `${carInfo?.model_name}${carInfo?.version !== null ? " " + carInfo?.version : ""}`
                )
                ?.avgAmount?.toLocaleString()}{" "}
              &nbsp;/{t("days")}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

const sortCars = (avaiableCars, sortBy, top_models, groupPrice) => {
  let result = avaiableCars
    .filter((car) => {
      return !top_models?.includes(car.model_id);
    })
    .map((car) => {
      const model_name = `${car.model_name}${car.version ? ` ${car.version}` : ""}`;
      const groupPriceItem = groupPrice.find(
        (item) => item.model_name === model_name
      );
      if (groupPriceItem) {
        car.group_price = groupPriceItem.tax_amount;
      }
      return car;
    });

  if (sortBy === "low") {
    result.sort((a, b) => {
      return parseFloat(a.group_price) - parseFloat(b.group_price);
    });
  } else {
    result.sort((a, b) => {
      return parseFloat(b.group_price) - parseFloat(a.group_price);
    });
  }
  let topModelReverse = top_models?.reverse();

  topModelReverse.forEach((id) => {
    const car = avaiableCars.filter((car) => car.model_id === id);
    if (car) {
      result.unshift(...car);
    }
  });

  return result;
};

const StationContent = ({
  zoneDetail,
  carList,
  noCarList,
  groupPrice,
  top_models,
  drawerRef,
  isLoading,
  isInstantZone,
}) => {
  const { t } = useTranslation("common");
  const router = useRouter();
  const { selectedDays } = appStore;

  const isAfterNewYear = isAfter(new Date(), new Date("2025/01/27 23:59:59"));

  if (isLoading) {
    <Flex>
      <Spinner />
    </Flex>;
  }

  return (
    <>
      {/* <Flex
        display={{ base: "flex", md: "none" }}
        justify="center"
        align="center"
        h="20px"
      >
        <Box w="36px" h="4px" borderRadius="2px" bg="rgba(0, 0, 0, .25)"></Box>
      </Flex> */}
      <Box overflow="scroll" h="100%" ref={drawerRef} pb="16px">
        <Box pt={{ base: "0px", md: "24px" }}>
          {isInstantZone && (
            <Flex
              w="fit-content"
              mx={{ base: "16px", md: "24px" }}
              borderRadius="8px"
              bg="#F3F4F9"
              px="8px"
              py="4px"
            >
              <GCPImage
                w="20px"
                mr="4px"
                src="/images/icons/instantIcon.png"
                alt="icon"
              />
              <Text fontSize="14px" lineHeight="20px" color="#333333">
                {t("Station_quick_label")}
              </Text>
            </Flex>
          )}
          <Flex
            pb="24px"
            px={{ base: "16px", md: "24px" }}
            align="center"
            mt={{ base: isInstantZone ? "8px" : "0px", md: "8px" }}
          >
            <Box flex="1" mr="16px">
              <Text fontSize="24px" lineHeight="32px" fontWeight="700">
                {zoneDetail?.level_3 || ""}
              </Text>
              <Box mt="4px">
                <Text
                  as="span"
                  fontSize="14px"
                  lineHeight="20px"
                  color="#16B1A8"
                >
                  {t("Station_open_label")}{" "}
                </Text>
                <Text
                  as="span"
                  fontSize="14px"
                  lineHeight="20px"
                  color="#748598"
                >
                  ˙ {zoneDetail?.openSt} - {zoneDetail?.openEd}
                  {t("Station_close_label")}
                </Text>
              </Box>
              <Text mt="4px" fontSize="14px" lineHeight="20px" color="#748598">
                {zoneDetail?.description || ""}
              </Text>
            </Box>
            <Flex
              flexDir="column"
              w="83.5px"
              h="64px"
              bg="#F3F4F9"
              borderRadius="8px"
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              _hover={{ bg: "#E1E3EA" }}
              onClick={() => {
                window.open(
                  `https://www.google.com/maps/search/?api=1&query=${zoneDetail.lat},${zoneDetail.lng}`,
                  "_blank"
                );
              }}
            >
              <GCPImage
                src="/images/icons/navigateRounded.svg"
                alt="location"
                w="19.5px"
                h="19.5px"
              />
              <Text mt="4px" fontSize="14px" lineHeight="20px" color="#333333">
                {t("Station_navigate")}
              </Text>
            </Flex>
          </Flex>

          {/* 春節 banner */}
          {!isAfterNewYear && (
            <Box
              cursor="pointer"
              onClick={() => {
                router.push({
                  pathname: "/newYear",
                  query: router.query,
                });
              }}
            >
              <NewYearBanner />
            </Box>
          )}

          <Box w="100%" h="8px" mt="24px" bg="#F3F4F9"></Box>
        </Box>
        <Box overflow="scroll" px={{ base: "16px", md: "24px" }} py="24px">
          <Flex
            align="center"
            justify="space-between"
            p="16px"
            bg="#B23025"
            borderRadius="8px"
            mb="24px"
            w="100%"
          >
            <Flex align="center">
              <GCPImage
                w="49px"
                src="/images/icons/Empty.png"
                alt="icon"
                mr="16px"
              />
              <Box>
                <Text
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight="700"
                  color="white"
                  mb="4px"
                >
                  {t("Station_quick_box_title")}
                </Text>
                <Text fontSize="12px" lineHeight="16px" color="white">
                  {t("Station_quick_box_content")}
                </Text>
              </Box>
            </Flex>

            <Flex
              bg="white"
              h="32px"
              borderRadius="16px"
              px="16px"
              py="8px"
              align="center"
              justify="center"
              cursor="pointer"
              onClick={() => {
                sendAppierEvent("click_instant_rental_card", {
                  user_id: appStore.member.id,
                  phoneNo: appStore.member.phone,
                  user_name: appStore.member.fullname,
                  selected_zone_id: zoneDetail?.id,
                  selected_station: zoneDetail?.level_3,
                });

                const text = `您好，我想要快速租車\n預約日期和時間：\n取車地點（限快取站）：${zoneDetail?.level_3}\n想要預約的車款：`;

                const urlEncodedText = encodeURIComponent(text);

                if (deviceType.toLowerCase() == "mobile") {
                  window.open(
                    `https://line.me/R/oaMessage/@495mteoq/?${urlEncodedText}`,
                    "_blank"
                  );
                } else {
                  window.open("https://lin.ee/l6p1dTh", "_blank");
                }
              }}
            >
              <Text
                fontSize="12px"
                lineHeight="16px"
                fontWeight="700"
                color="#B23025"
                whiteSpace="nowrap"
              >
                {t("Station_quick_box_btn")}
              </Text>
            </Flex>
          </Flex>

          <Text mb="16px" fontSize="20px" lineHeight="28px" fontWeight="700">
            {zoneDetail?.availableCarList.length === 0
              ? t("noCar")
              : `${zoneDetail?.availableCarList.length} ${t("carUnit")} ${t("canOrderCar")}`}
          </Text>
          <Box>
            {!!Object.keys(carList).length &&
              Object.keys(carList).length > 0 &&
              Object.keys(carList).map((key) => {
                const carInfo = carList[key][0];
                return (
                  <CarGroup
                    key={key}
                    carInfo={carInfo}
                    carList={carList[key]}
                    groupPrice={groupPrice}
                    top_models={top_models}
                    zoneDetail={zoneDetail}
                  />
                );
              })}

            {!!Object.keys(noCarList).length &&
              Object.keys(noCarList).length > 0 &&
              Object.keys(noCarList).map((key) => {
                const hasCar = carList[key];
                if (hasCar) {
                  return;
                }
                const carInfo = noCarList[key][0];
                return (
                  <NoCarGroup
                    key={key}
                    carInfo={carInfo}
                    carList={noCarList[key]}
                    groupPrice={groupPrice}
                    top_models={top_models}
                  />
                );
              })}
          </Box>
        </Box>
      </Box>
    </>
  );
};

const StationInfoDrawer = ({
  stations,
  deliveryZones,
  groupPrice,
  top_models,
  isAllRangeTimesLoaded,
  configs,
  cars,
  allRangeTimes,
  instant_zone_ids,
}) => {
  const { t } = useTranslation("common");
  const isMdUp = useBreakpointValue({ base: false, md: true });
  const { tempSelectedZoneId, selectedDays, modelsFilter } = appStore;
  const [isOpen, setIsOpen] = useState(false);
  const [zoneDetail, setZoneDetail] = useState(null);
  const [carList, setCarList] = useState([]);
  const [noCarList, setNoCarList] = useState([]);
  const [isFull, setIsFull] = useState(false);
  const drawerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const isInstantZone = useMemo(() => {
    return instant_zone_ids.includes(zoneDetail?.id);
  }, [instant_zone_ids, zoneDetail?.id]);

  useEffect(() => {
    if (
      !isAllRangeTimesLoaded ||
      tempSelectedZoneId === "" ||
      tempSelectedZoneId === null
    ) {
      onCloseSheet();
      return;
    }

    setIsLoading(true);
    setIsOpen(true);
    const initStations = async () => {
      const thisStation = await Promise.all(
        deliveryZones
          .filter(
            (zone) => zone.id.toString() === tempSelectedZoneId.toString()
          )
          .map((zone) => {
            // 從 configs 中找出有支援該 zone 的 config
            const supportConfigs = configs
              .filter((config) =>
                config.support_zones.some(
                  (supportZone) =>
                    supportZone.id.toString() === zone.id.toString()
                )
              )
              .map((config) => {
                const car = cars.filter((_car) => {
                  return config.car_ids.includes(_car.id);
                });
                return {
                  ...config,
                  car,
                };
              });

            let openSt = "";
            let openEd = "";
            supportConfigs.forEach((config) => {
              const thisZone = config.support_zones.find(
                (_zone) => _zone.id.toString() === zone.id.toString()
              );
              if (thisZone) {
                if (thisZone.business_start_hour < openSt || openSt === "") {
                  openSt = thisZone.business_start_hour;
                }
                if (thisZone.business_end_hour > openEd || openEd === "") {
                  openEd = thisZone.business_end_hour;
                }
              }
            });

            let carIds = [];

            supportConfigs.forEach((config) => {
              carIds = [...carIds, ...config.car_ids];
            });

            const startDate = selectedDays[0].format("YYYY/MM/DD");
            const endDate = selectedDays[1].format("YYYY/MM/DD");

            const availableCarIds = getAvailableCarIdsForStation(
              startDate,
              endDate,
              allRangeTimes,
              carIds,
              configs,
              zone.id
            );

            // console.log("availableCarIds", availableCarIds);

            let lowestModel = {};
            if (availableCarIds.length === 0) {
              if (supportConfigs.length !== 0) {
                const allCars = supportConfigs
                  .map((config) => config.car)
                  .flat();
                lowestModel = allCars.reduce((lowest, current) => {
                  return lowest.price < current.price ? lowest : current;
                });
              }
            } else {
              const allCars = availableCarIds.map((carId) => {
                return supportConfigs
                  .map((config) => config.car)
                  .flat()
                  .find((car) => car.id === carId);
              });

              lowestModel = allCars.reduce((lowest, current) => {
                return lowest.price < current.price ? lowest : current;
              });
            }

            const availableCarList = cars.filter(
              (car) =>
                availableCarIds.includes(car.id) &&
                modelsFilter[car.model_name.replace(" ", "")]
            );

            const allSupportThisZoneCarList = cars.filter((car) => {
              return carIds.includes(car.id);
            });
            const noCarList = allSupportThisZoneCarList.filter((car) => {
              return (
                !availableCarIds.includes(car.id) &&
                modelsFilter[car.model_name.replace(" ", "")]
              );
            });

            return {
              ...zone,
              supportConfigs,
              lowestModel: lowestModel || {},
              openSt,
              openEd,
              hasAvailableCar: availableCarList.length > 0,
              availableCarList,
              noCarList,
              allSupportThisZoneCarList,
            };
          })
      );

      setZoneDetail(thisStation[0]);

      setCarList(
        reduceCarsToOneLevelAddVersion(
          sortCars(
            thisStation[0].availableCarList,
            "low",
            top_models,
            groupPrice
          )
        )
      );

      setNoCarList(
        reduceCarsToOneLevelAddVersion(
          sortCars(thisStation[0].noCarList, "high", top_models, groupPrice)
        )
      );
      setIsLoading(false);
    };

    initStations();
  }, [
    allRangeTimes,
    cars,
    configs,
    deliveryZones,
    isAllRangeTimesLoaded,
    selectedDays,
    modelsFilter,
    tempSelectedZoneId,
    top_models,
  ]);

  // useEffect(() => {
  //   if (tempSelectedZoneId !== null && tempSelectedZoneId !== "") {
  //     const zone = deliveryZones.find(
  //       (zone) => zone.id.toString() === tempSelectedZoneId.toString()
  //     );
  //     if (zone.level_3 !== null) {
  //       setIsOpen(true);
  //       const detail = stations.find(
  //         (zone) => zone.id.toString() === tempSelectedZoneId.toString()
  //       );
  //       setZoneDetail(detail);

  //       const sortedAvailableCarList = sortCars(
  //         detail.availableCarList,
  //         "low",
  //         top_models
  //       );

  //       // console.log("sortedAvailableCarList", sortedAvailableCarList);
  //       setCarList(
  //         reduceCarsToOneLevelAddVersion(
  //           sortCars(detail.availableCarList, "low", top_models)
  //         )
  //       );

  //       setNoCarList(
  //         reduceCarsToOneLevelAddVersion(
  //           sortCars(detail.noCarList, "high", top_models)
  //         )
  //       );
  //     }
  //   } else {
  //     onCloseSheet();
  //   }
  // }, [deliveryZones, stations, tempSelectedZoneId, selectedDays]);

  const onCloseSheet = () => {
    setIsOpen(false);
    setIsFull(false);
    appStore.tempSelectedZoneId = null;
    if (drawerRef.current) {
      drawerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <Box>
      <Box
        display={{ base: "none", md: "block" }}
        zIndex="998"
        w="409px"
        minW="409px"
        // h={{ base: "calc(100vh - 199px)", lg: "calc(100% - 221px)" }}
        h="100%"
        bg="white"
        pos="absolute"
        left="0"
        top="0px"
        // top={{ base: "", sm: "200p", lg: "225px" }}
        transition="all 0.3s ease-in-out"
        transform={isOpen ? "translateX(0)" : "translateX(-100%)"}
        boxShadow="4px 0px 6px 0px rgba(0, 0, 0, 0.1)"
      >
        {/* drawer 開啟關閉按鈕 */}
        {zoneDetail?.level_3 && (
          <Box
            zIndex="99"
            w="40px"
            h="40px"
            pos="absolute"
            top="16px"
            left="422px"
            p="8px"
            boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1)"
            bg="white"
            borderRadius="8px"
            cursor="pointer"
            onClick={() => {
              setIsOpen((state) => !state);
            }}
          >
            <GCPImage
              src={
                isOpen
                  ? "/images/icons/chevronLeft.svg"
                  : "/images/icons/chevronRight.svg"
              }
              alt="close"
              w="24px"
              h="24px"
            />
          </Box>
        )}

        <StationContent
          zoneDetail={zoneDetail}
          carList={carList}
          noCarList={noCarList}
          groupPrice={groupPrice}
          top_models={top_models}
          isLoading={isLoading}
          isInstantZone={isInstantZone}
        />
      </Box>

      {!isMdUp && (
        <Box display={{ base: "block", md: "none" }} pos="relative">
          {/* 改寫成自定義 Drawer */}
          <Box
            w="100vw"
            h={isFull ? "calc(100dvh - 199px)" : "40svh"}
            borderTopRadius="16px"
            bg="white"
            pos="absolute"
            left="0"
            right="0"
            top={isFull ? "0px" : "calc(60vh - 199px)"}
            zIndex="999"
            transition="all 0.3s ease-in-out"
            transform={isOpen ? "translateY(0)" : "translateY(100%)"}
            overflow="hidden"
          >
            <Box h="48px" p="16px" display="flex" justifyContent="end">
              <GCPImage
                src="/images/icons/close.jpg"
                alt="close"
                w="24px"
                h="24px"
                onClick={onCloseSheet}
              />
            </Box>

            <Box
              h="100%"
              overflow="scroll"
              pb="48px"
              onTouchMove={(e) => {
                if (!isFull) {
                  setIsFull(true);
                }
              }}
              onClick={(e) => {
                if (!isFull) {
                  setIsFull(true);
                }
              }}
            >
              <StationContent
                zoneDetail={zoneDetail}
                carList={carList}
                noCarList={noCarList}
                groupPrice={groupPrice}
                top_models={top_models}
                drawerRef={drawerRef}
                isLoading={isLoading}
                isInstantZone={isInstantZone}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default StationInfoDrawer;
